import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  AccountMenu,
  AccountMenuDivider,
  AccountMenuHeader,
  AccountMenuItem,
  AccountMenuSection,
  Button,
  Icon,
  Wordmark,
  BrandMark,
  EmptyState,
  Spinner,
  ShellNav,
  ShellTopbar,
  ShellTopbarActions,
  PlatformIcon,
  ProBadge,
  compactNumber,
  platformLabel } from "@cntrl/ui";
import { useMe, useNavigation } from "../../app/hooks/useSession.js";
import { InboxButton } from "../../features/inbox/InboxButton.js";
import { useRosterShowLink } from "../../app/showLink.js";
import { hasInbox } from "../../app/auth/session.js";
import { useOrg, orgInitials, orgTypeLabel } from "../../app/hooks/useOrg.js";
import { roleLabelFrom, useSeatRoles } from "../../app/hooks/useSeatRoles.js";
import { useArtistPhotoUrl } from "../../features/org/artistPhoto.js";
import { useRosterHeaderActionStore } from "../../app/rosterHeaderAction.js";
import { useRosterHeaderPillsStore } from "../../app/rosterHeaderPills.js";
import { useRosterArtists, useResolvedOrg, orgHomeContext, subviewLabel, type RosterArtistView } from "../../app/hooks/useRoster.js";
import { OrgProvider, orgContextFromNav } from "../../app/orgContext.js";
import { LinkArtistModal } from "../../features/org/LinkArtistModal.js";
import { MyProfileModal } from "../../features/contacts/MyProfileModal.js";
import { PersonAvatar } from "../../features/shared/PersonAvatar.js";
import { TeamModal } from "../../features/org/TeamModal.js";
import { ManagerModeMark } from "../../features/org/ManagerModeMark.js";
import { OrgSettingsModal } from "../../features/org/OrgSettingsModal.js";
import { FixtureSwitchButton } from "../../features/dev/FixtureSwitchButton.js";
import { FixtureSwitchDialog } from "../../features/dev/FixtureSwitchDialog.js";
import { useActiveOrg } from "../../app/activeOrg.js";
import { useTeamPanel } from "../../app/teamPanel.js";
import { setOrgLens } from "../../app/orgLens.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { NotificationBell } from "../../features/notifications/NotificationBell.js";
import { useAuth } from "../../app/auth/useAuth.js";
import { rosterTabLabel, isScopedTab, buildRosterNavItems } from "./rosterTabs.js";
import { RosterMobileTabBar } from "./RosterMobileTabBar.js";
import { RosterMoreSheet } from "./RosterMoreSheet.js";
import { RosterAccountSheet } from "./RosterAccountSheet.js";
import { isSingleArtistMobileLens } from "../../app/rosterLastArtist.js";
import { Avatar } from "../mobile/Avatar.js";
import { initials } from "../mobile/accountRoles.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import styles from "./RosterShell.module.scss";
import { PendingInvitesMenuItem } from "../../features/invites/PendingInvitesMenuItem.js";
import { usePhoneLayout } from "../../app/phoneLayout.js";

export interface RosterShellProps {
  children: React.ReactNode;
  /** The focused artist (from `/roster/artists/:artistId`), or undefined on All-roster / org-level routes. */
  focusedArtistId?: string;
  /** The active module tab (dashboard/music/releases/…), from the route. */
  activeTab?: string;
  /** Select an artist (or null = All-roster). The layout runs the switchRosterArtist SCORCH here. */
  onSelectArtist: (bandId: string | null) => void;
  /** Navigate within the current scope to a module tab. */
  onTab: (tabKey: string) => void;
  /** Generic SPA navigation (settings/onboarding). Team is an overlay here, not a route. */
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

/**
 * RosterShell — the org-root chrome: a sticky translucent topbar (brand · artist-scoped tab strip ·
 * subview chip · bell · org account chip), a 240px artist rail, and a context-header slot above the routed
 * module. Chrome only, zero feature logic. The SUBVIEW + the tab-set are SERVER data (the org navigation
 * document, — the client holds no role matrix; the jedi `?as=` override is dead). The org-lens is activated
 * here so every band-scoped read under the shell resolves the `/orgs/{orgId}/bands/{bandId}` mirror.
 * Loading / error / no-org / born-blank states are designed, not blank.
 */
export function RosterShell({
  children,
  focusedArtistId,
  activeTab,
  onSelectArtist,
  onTab,
  onNavigate,
  onLogout,
}: RosterShellProps) {
  const me = useMe();
  const { orgId, subview, seat, orgSeats } = useResolvedOrg();
  // The chat's Day of Show link from the roster: the lens's show page, and none on a phone (showLink.ts).
  const showHref = useRosterShowLink();
  const nav = useNavigation(orgId ? orgHomeContext(orgId) : undefined);
  const showInbox = hasInbox(me.data);
  const artists = useRosterArtists(orgId);
  const [linkingArtist, setLinkingArtist] = useState(false);
  const [fixtureSwitchOpen, setFixtureSwitchOpen] = useState(false);
  // Team opens IN PLACE, the way Settings does on the artist shell — the routed `/roster/team` page stays
  // reachable for a bookmark, so this is a second CHROME, never a second implementation.
  const [teamOpen, setTeamOpen] = useState(false);
  // …and the "Manage team" doors elsewhere in the roster ask for it through the same one-field store the
  // Socials strip uses to ask the artist shell for a Settings tab.
  // Settings opens in place too — the routed `/roster/settings` page stays for a bookmark, exactly as
  // `/settings/*` does behind the artist shell's gear.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // …and so does the person's OWN profile (PO, 2026-09-01). An org member's profile is a profile: the same
  // `CrewProfileModal` a band member gets, with the organizations they sit in and the artists they reach on
  // its rail. This shell had no door to one at all — org profile and password were the whole account menu,
  // which is gap 3 of the roster audit.
  const [profileOpen, setProfileOpen] = useState(false);
  const teamRequested = useTeamPanel((s) => s.requested);
  const consumeTeamRequest = useTeamPanel((s) => s.consume);
  useEffect(() => {
    if (!teamRequested) return;
    setTeamOpen(true);
    consumeTeamRequest();
  }, [teamRequested, consumeTeamRequest]);

  // Activate the org-lens for the shell's whole life: while mounted, band-direct reads splice
  // /orgs/{orgId}. Cleared on unmount so a later artist-shell read is never rewritten.
  useEffect(() => {
    setOrgLens(orgId);
    return () => setOrgLens(undefined);
  }, [orgId]);

  // The shell's realtime groups: the person's user:{id} (notifications, latched so it survives an
  // artist switch) + the org:{orgId} group (reach-revoked eviction rides this). The focused band:{id} group is
  // joined declaratively by the artist layout.
  const [stablePersonId, setStablePersonId] = useState<string>();
  useEffect(() => {
    const id = me.data?.person?.personId;
    if (id) setStablePersonId(id);
  }, [me.data]);
  useLiveGroup(stablePersonId ? `user:${stablePersonId}` : null);
  useLiveGroup(orgId ? `org:${orgId}` : null);

  const rosterTabs = useMemo(() => nav.data?.rosterTabs ?? [], [nav.data]);

  // The grouped IA. ShellNav owns the menu machinery (hover/click open, Escape + outside-press close);
  // this only decides WHICH items exist, from the server's ordered tab list.
  const navItems = useMemo(() => buildRosterNavItems(rosterTabs, subview), [rosterTabs, subview]);
  // The org-level screens (RosterHomeScreen/OrgTeamScreen/OrgSettingsScreen) render under this OrgContext (the
  // org-home nav — roster:* tokens). The artist-scoped routes nest a BandProvider under it (RosterBandProvider).
  const orgContext = useMemo(() => (nav.data ? orgContextFromNav(nav.data) : null), [nav.data]);
  const focusedArtist = focusedArtistId
    ? artists.data?.find((a) => a.bandId === focusedArtistId)
    : undefined;
  // Unconditional — a hook cannot be, and it resolves to null with nothing focused. Same org-lens-safe
  // arrangement the rail rows use, so the context header and the rail draw one artist one way.
  const focusedPhoto = useArtistPhotoUrl(focusedArtist?.bandId, focusedArtist?.avatarMediaId);
  const headerAction = useRosterHeaderActionStore((s) => s.action);
  const headerPills = useRosterHeaderPillsStore((s) => s.pills);

  // Mobile roster chrome (ART-155): below `lg` the org lens gets a bottom-tab shell instead of the desktop
  // 240px-rail layout. Same server org tab set (`nav.mobile`), same routing (`onTab`), same overlays — only
  // the chrome layout swaps, so every load-bearing behaviour (org-lens activation, OrgProvider, realtime,
  // the modals) stays in ONE place and cannot drift between the two.
  const wide = !usePhoneLayout();
  const [moreOpen, setMoreOpen] = useState(false);
  const [accountSheetOpen, setAccountSheetOpen] = useState(false);
  const mobileTabs = useMemo(() => nav.data?.mobile?.tabs ?? [], [nav.data]);
  const mobileMore = useMemo(() => nav.data?.mobile?.more ?? [], [nav.data]);
  // Stable so the memoized RosterMobileTabBar repaints only when its tabs/active key change, not on every
  // sheet or dialog toggle. Defined here (not in the mobile branch below) to satisfy the rules of hooks.
  const openMore = useCallback(() => setMoreOpen(true), []);
  const onSelectTab = useCallback(
    (key: string) => {
      if (key === "home") onTab("dashboard");
      else if (key === "inbox") onNavigate("/roster/messages");
      // The phone's Schedule is the CALENDAR on every lens, as it is on the band's phone (`/calendar`) — never
      // the desktop booking cockpit `rosterTabRoute` maps the desktop `schedule` tab to (mobile books nothing).
      else if (key === "schedule") onTab("calendar");
      else onTab(key);
    },
    [onTab, onNavigate],
  );
  // The person's face for the mobile navbar's avatar button — the same bell + avatar → sheet shape the band
  // mobile header uses, rather than a desktop-style account chip. `person.avatarUrl` is the dead CDN slot, so
  // the bytes resolve from `avatarMediaId` through the authenticated avatar route.
  const selfAvatarUrl = useAvatarUrl("person", me.data?.person?.personId, me.data?.person?.avatarMediaId);

  if (me.isError) {
    return (
      <ShellFrame onLogout={onLogout}>
        <EmptyState
          title="We couldn't load your account"
          description="Check your connection and try again."
          action={<Button onClick={() => me.refetch()}>Retry</Button>}
        />
      </ShellFrame>
    );
  }

  // session resolved with no org seat — guidance, never a blank surface.
  if (!me.isLoading && !orgId) {
    return (
      <ShellFrame onLogout={onLogout}>
        <EmptyState
          title="No organization yet"
          description="Labels, management companies and booking agencies join Artist CNTRL by applying."
          action={<Button onClick={() => onNavigate("/onboarding/create-org")}>Apply as a company</Button>}
        />
      </ShellFrame>
    );
  }

  // Shared across BOTH chromes — the org-scoped modals (never layout-dependent) and the routed content body.
  const overlays = (
    <>
      {linkingArtist && <LinkArtistModal orgId={orgId} onClose={() => setLinkingArtist(false)} />}
      {/* Root level, not the actions row — `Modal` does not portal and the topbar is a stacking context. */}
      {fixtureSwitchOpen && (
        <FixtureSwitchDialog onClose={() => setFixtureSwitchOpen(false)} onNavigate={onNavigate} />
      )}
      {/* Every Team read is org-scoped, so it mounts UNDER the resolved OrgContext (the SettingsModal precedent). */}
      {teamOpen && orgContext && (
        <OrgProvider value={orgContext}>
          <TeamModal onClose={() => setTeamOpen(false)} />
        </OrgProvider>
      )}
      {settingsOpen && orgContext && (
        <OrgProvider value={orgContext}>
          {/* The focused artist rides through so the dialog can offer the "Artist plan" tab (ART-142). */}
          <OrgSettingsModal onClose={() => setSettingsOpen(false)} artistId={focusedArtistId ?? undefined} />
        </OrgProvider>
      )}
      {/* No OrgProvider: a person profile is PERSON-scoped end to end (`/users/*`, `/me/*`). */}
      {profileOpen && <MyProfileModal onClose={() => setProfileOpen(false)} />}
    </>
  );

  // A roster module ALWAYS mounts under a resolved OrgProvider — the org-level screens require it
  // (useOrgContext throws otherwise). The artist-scoped modules add a BandProvider beneath it.
  const body = nav.isError ? (
    <EmptyState
      title="We couldn't load your organization"
      description="Check your connection and try again."
      action={<Button onClick={() => nav.refetch()}>Retry</Button>}
    />
  ) : orgContext ? (
    <OrgProvider value={orgContext}>{children}</OrgProvider>
  ) : (
    <div className={styles.contentLoading} role="status" aria-live="polite">
      <Spinner size={22} label="Loading your roster…" />
    </div>
  );

  // ---- Mobile roster chrome (ART-155): a bottom-tab shell instead of the 240px-rail layout ----
  if (!wide && orgId) {
    // The mobile bar speaks its own key vocabulary (server "home", not the route's "dashboard"); the shell
    // owns the mapping to roster routing so the bar needs no route knowledge.
    // `calendar` is where the phone's Schedule tab lands (see `onSelectTab`), so it lights that tab.
    const activeKey = activeTab === "dashboard" ? "home" : activeTab === "calendar" ? "schedule" : activeTab;
    return (
      <div className={styles.mShell}>
        <header className={styles.mHeader}>
          <BrandOrg orgId={orgId} artistCount={null} compact />
          {/* Fundamentally the band mobile header's right side: (view-as) · bell · avatar → sheet. Inbox is a
              bottom TAB here (management), not a header button, and the account is a sheet, not a desktop chip. */}
          <div className={styles.mHeaderActions}>
            <FixtureSwitchButton open={fixtureSwitchOpen} onOpen={() => setFixtureSwitchOpen(true)} />
            {/* Never the anchored panel on the phone — navigate to the page (the band mobile header rule). */}
            <NotificationBell allowPanel={false} onNavigate={onNavigate} />
            <button
              type="button"
              className={styles.mAvatarBtn}
              aria-haspopup="dialog"
              aria-expanded={accountSheetOpen}
              aria-label="Account menu"
              onClick={() => setAccountSheetOpen(true)}
            >
              <Avatar url={selfAvatarUrl} mark={initials(me.data?.person?.displayName || me.data?.person?.firstName)} />
            </button>
          </div>
        </header>
        <main className={styles.mContent}>
          {/* No "‹ All roster" breadcrumb: a phone has no All-roster view on any lens (ART-358 / ART-361 /
              ART-360) — the artist is switched from the avatar sheet. */}
          {body}
        </main>
        {mobileTabs.length > 0 && (
          <RosterMobileTabBar
            tabs={mobileTabs}
            activeKey={activeKey}
            onSelect={onSelectTab}
            onOpenMore={openMore}
          />
        )}
        {/* Kept MOUNTED and toggled (the band More sheet's warm-mount shape) so opening is instant. */}
        <RosterMoreSheet
          rows={mobileMore}
          open={moreOpen}
          onOpenSettings={() => {
            setMoreOpen(false);
            setSettingsOpen(true);
          }}
          onClose={() => setMoreOpen(false)}
        />
        {accountSheetOpen && (
          <RosterAccountSheet
            person={me.data?.person}
            activeOrgId={orgId}
            orgSeats={orgSeats}
            artists={isSingleArtistMobileLens(subview) ? (artists.data ?? []) : undefined}
            activeArtistId={focusedArtistId}
            orgName={seat?.displayName ?? undefined}
            subview={subview}
            onSelectArtist={onSelectArtist}
            canManageTeam={nav.data?.capabilities?.["roster:manage_seats"] === true}
            onViewProfile={() => setProfileOpen(true)}
            onOpenTeam={() => setTeamOpen(true)}
            onOpenSettings={() => setSettingsOpen(true)}
            onNavigate={onNavigate}
            onLogout={onLogout}
            onClose={() => setAccountSheetOpen(false)}
          />
        )}
        {overlays}
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <ShellTopbar>
        <BrandOrg orgId={orgId} artistCount={artists.isLoading || !orgId ? null : (artists.data?.length ?? null)} />
        <ShellNav
          label="Roster tabs"
          items={navItems}
          loading={nav.isLoading}
          closeKey={activeTab}
          isActive={(key) => key === activeTab}
          onSelect={onTab}
          // A scoped tab with no artist picked is dimmed but still operable — it routes to the
          // artist-required gate, which explains itself.
          itemProps={(key) =>
            isScopedTab(key) && !focusedArtistId ? { "data-scoped-empty": "true" } : undefined
          }
        />
        <ShellTopbarActions>
          {subview && <span className={styles.subviewChip}>{subviewLabel(subview)}</span>}
          <FixtureSwitchButton open={fixtureSwitchOpen} onOpen={() => setFixtureSwitchOpen(true)} />
          {/* MANAGEMENT gets the inbox button; label and booking do not (PO, 2026-09-11). The same `hasInbox` the
              artist shell reads and the route guard enforces — one server answer, three consumers, so the
              door and the gate cannot come apart the way they did for the band account. */}
          {showInbox && (
            // The lens carries the artist in the path, so ANY band a chat is for resolves here — there is
            // no mounted band to disagree with, and crossing to an artist-shell URL would land an org seat
            // on "No band selected".
            <InboxButton route="/roster/messages" onNavigate={onNavigate} showHref={showHref} />
          )}
          <NotificationBell onNavigate={onNavigate} />
          {orgId && (
            <RosterAccountChip
              orgId={orgId}
              seatRole={seat?.titleKey}
              orgSeats={orgSeats}
              canManageTeam={nav.data?.capabilities?.["roster:manage_seats"] === true}
              onViewProfile={() => setProfileOpen(true)}
              onOpenTeam={() => setTeamOpen(true)}
              onOpenSettings={() => setSettingsOpen(true)}
              onNavigate={onNavigate}
              onLogout={onLogout}
            />
          )}
        </ShellTopbarActions>
      </ShellTopbar>

      <div className={styles.layout}>
        <ArtistRail
          orgId={orgId}
          artists={artists.data ?? []}
          // While /me is still resolving, orgId is undefined ⇒ the artists query is DISABLED (never "loading"),
          // so treat the un-resolved org as loading — never flash born-blank "No artists yet" before the org lands.
          isLoading={!orgId || artists.isLoading}
          isError={artists.isError}
          focusedArtistId={focusedArtistId}
          onSelect={onSelectArtist}
          onRetry={() => artists.refetch()}
          // "Add artist" opens the LINK modal (PO, 2026-08-20 — the code handshake), not the team screen.
          onAdd={() => setLinkingArtist(true)}
        />
        {overlays}
        <main className={styles.content}>
          {focusedArtist && (
            <div className={styles.contextHeader}>
              {/* The artist's own PHOTO, not their initials. This drew initials for every artist on every
                  lens while the rail two inches away drew the picture — off the same `avatarMediaId`, which
                  was on the row all along. `useArtistPhotoUrl` is the org-lens-safe resolution both now
                  share; a band-direct media route is one an org seat cannot resolve and it fails silently. */}
              {focusedPhoto ? (
                <img className={styles.ctxAvatar} src={focusedPhoto} alt="" data-photo="" />
              ) : (
                <span className={styles.ctxAvatar} aria-hidden="true">
                  {orgInitials(focusedArtist.name)}
                </span>
              )}
              <div className={styles.ctxText}>
                <span className={styles.ctxEyebrow}>
                  {rosterTabLabel(activeTab ?? "dashboard", subview)} · {subviewLabel(subview)}
                </span>
                <span className={styles.ctxNameRow}>
                  <span className={styles.ctxName}>{focusedArtist.name || "Artist"}</span>
                  {/* ART-353: the artist's manager mode, served on management rows only. */}
                  <ManagerModeMark mode={focusedArtist.managerMode} size={20} />
                </span>
                {/* The rail's own audience line — "674m followers · 530.9m mo", absent entirely when
                    nothing has landed (never a fabricated 0, and never the org provenance in its place —
                    see `artistMetaLine`). It moved here when the pages dropped their second header
                    (PO, 2026-09-02): the figures were on that header, and the shell is the one header now. */}
                {artistMetaLine(focusedArtist) && (
                  <span className={styles.ctxMeta}>{artistMetaLine(focusedArtist)}</span>
                )}
              </div>
              {/* The routed page's own action, registered for as long as it is mounted — Add task on
                  Management, Refresh on Label, New offer on Booking. The shell renders it and knows nothing
                  about what it does; see `rosterHeaderAction.ts` for why that split. */}
              {/* The artist's social following, one pill per connected platform (ART-334/332) — registered by the
                  dashboard page, which holds the numbers. */}
              {headerPills && headerPills.length > 0 && (
                <ul className={styles.ctxPills} aria-label="Followers by platform">
                  {headerPills.map((p) => (
                    <li key={p.platform} className={styles.ctxPill}>
                      <PlatformIcon platform={p.platform} size={20} label={platformLabel(p.platform)} />
                      {compactNumber(p.followers)}
                    </li>
                  ))}
                </ul>
              )}
              {headerAction && (
                <Button
                  className={styles.ctxAction}
                  size={headerAction.size}
                  variant={headerAction.variant}
                  onClick={headerAction.onClick}
                >
                  {headerAction.icon && <headerAction.icon size={16} aria-hidden />} {headerAction.label}
                </Button>
              )}
            </div>
          )}
          {body}
        </main>
      </div>
    </div>
  );
}

/**
 * The topbar's leading slot. Under the roster shell it is the AC mark + the ORG, not the wordmark: whose
 * roster this is and how big it is — the lockup legacy carried at the top of its full-height rail
 * (`.rv-rail__head`: the crimson `BrandMark`, the org name, "Label · 3 artists"), which in this layout is
 * that same top-left corner one row up. `ShellFrame` keeps the wordmark, because its error / no-org states
 * have no org to name.
 */
function BrandOrg({
  orgId,
  artistCount,
  compact = false,
}: {
  orgId: string | undefined;
  artistCount: number | null;
  /** ONE line — mark + org name, no "Label · N artists" meta line. The mobile navbar left, matching the band
   *  mobile header's single-line lockup. */
  compact?: boolean;
}) {
  const org = useOrg(orgId);
  const kind = orgTypeLabel(org.data?.orgType);
  const name = org.data?.name || "Your organization";
  if (compact) {
    // A tight, vertically-centred flex row (the band header's `.headerBrand`): the mark inline, NOT in the
    // desktop lockup's fixed 30px block box — that box top-left-aligns a 26px mark and adds phantom gap.
    return (
      <div className={styles.brandCompact}>
        <BrandMark size={26} />
        <span className={styles.brandName}>{name}</span>
      </div>
    );
  }
  // While the roster is still resolving the count is OMITTED, not shown as 0 — a fresh org really can have
  // none, so a flashed "0 artists" is indistinguishable from the born-blank truth.
  const meta =
    artistCount == null ? kind : `${kind} · ${artistCount} ${artistCount === 1 ? "artist" : "artists"}`;
  return (
    <div className={styles.brand}>
      <span className={styles.brandMark}>
        <BrandMark size={30} />
      </span>
      <span className={styles.brandText}>
        <span className={styles.brandName}>{name}</span>
        <span className={styles.brandMeta}>{meta}</span>
      </span>
    </div>
  );
}

// ---- The 240px artist rail -----------------------------------------------------------------------

interface ArtistRailProps {
  orgId: string | undefined;
  artists: RosterArtistView[];
  isLoading: boolean;
  isError: boolean;
  focusedArtistId?: string;
  onSelect: (bandId: string | null) => void;
  onRetry: () => void;
  onAdd: () => void;
}

/**
 * The rail-search shortcut hint. It names the modifier the listener below ACTUALLY accepts on this
 * platform — a badge advertising a chord the page does not honour is the dead-affordance failure, and legacy's
 * own ⌘K was cosmetic (it had no handler at all; roster.audit §7).
 */
const SEARCH_HINT =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? "⌘K" : "^K";

/**
 * The band's own photo in the rail. The FETCH is `useArtistPhotoUrl` — the one arrangement that rides
 * `bandScopedPath` and therefore the ORG-LENS mirror, since a band-direct `/bands/{id}/media/…` is a route an
 * org seat cannot resolve and fails SILENTLY (the hook degrades to `error:true` and the tile renders empty).
 * Each row resolves against its OWN band, exactly as the artist shell's `SeatMark` does.
 *
 * The rail keeps its own 34px chrome box rather than taking `ArtistMark`'s `Avatar`: this mark sits in the
 * shell, not in a content row, and is sized to the rail. Only the rule is shared.
 */
function RailArtistMark({ artist }: { artist: RosterArtistView }) {
  const url = useArtistPhotoUrl(artist.bandId ?? null, artist.avatarMediaId ?? null);
  return url ? (
    <img className={styles.avatar} src={url} alt="" data-photo="" />
  ) : (
    <span className={styles.avatar} aria-hidden="true">
      {orgInitials(artist.name)}
    </span>
  );
}

/**
 * The rail's audience sub-line — the figures the legacy rail carried ("584k followers · 942k mo"), served by
 * the rail read itself so the number is read off the projection it names.
 *
 * NOTHING LANDED ⇒ NOTHING RENDERED (PO, 2026-09-02). This used to fall back to the row's org provenance —
 * `responsibility`, then `orgTypeLabel(linkType)` — and neither says anything about the ARTIST. `linkType` is
 * the lens's own name, so it is the SAME word on every row a Label seat can see; `responsibility` is the
 * VIEWER's own, so it repeats down the rail too. Both put a non-audience fact in a slot the reader takes for
 * audience, and both appeared on exactly the rows with no data — marking the gap with a word unrelated to it
 * (a column of "Label" under every unseeded artist, which is how this was found in dev).
 *
 * The rule the fallback was protecting still stands, and is why this returns null rather than `0 followers`:
 * a fabricated zero enters a reader's series as a real measurement, the rule `HeadlineSnapshots` holds to.
 * Absent and zero stay different sentences — the fix is to say nothing, not to say something else.
 */
function artistMetaLine(a: RosterArtistView): string | null {
  const figures = [
    a.followers != null ? `${compactNumber(a.followers)} followers` : null,
    a.monthlyListeners != null ? `${compactNumber(a.monthlyListeners)} mo` : null,
  ].filter(Boolean);
  return figures.length > 0 ? figures.join(" · ") : null;
}

/** The four-swatch "all roster" glyph (the spec's `.rv-all__tile`), on brand tokens rather than its hexes. */
/** The All-roster mark: the org's own square logo once it has uploaded one (Organization settings, ART-337),
 *  else the four-colour default. Read off the org's `logoMediaId` through the shared avatar route. */
function AllRosterTile({ orgId }: { orgId: string | undefined }) {
  const org = useOrg(orgId);
  const logoUrl = useAvatarUrl("org", orgId, org.data?.logoMediaId ?? null);
  if (logoUrl) return <img className={styles.allLogo} src={logoUrl} alt="" />;
  return (
    <span className={styles.allTile} aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
  );
}

function ArtistRail({ orgId, artists, isLoading, isError, focusedArtistId, onSelect, onRetry, onAdd }: ArtistRailProps) {
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? artists.filter((a) => (a.name ?? "").toLowerCase().includes(q)) : artists;
  }, [artists, query]);

  // ⌘K / Ctrl-K focuses the rail search. The badge beside the field names whichever of the two this
  // platform uses, so the hint and the handler can never disagree.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.altKey || e.key.toLowerCase() !== "k" || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <aside className={styles.rail} aria-label="Roster">
      <div className={styles.search}>
        <Icon icon={Search} size={16} />
        <input
          ref={searchRef}
          type="search"
          placeholder="Search roster"
          // The accessible name is a CONTRACT, not a label: the booking pipeline's "+ New offer" focuses this
          // field by `input[aria-label="Search roster"]`, so a copy sweep here makes that button inert.
          aria-label="Search roster"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className={styles.kbd} aria-hidden="true">
          {SEARCH_HINT}
        </span>
      </div>

      <button
        type="button"
        className={styles.allCard}
        data-active={!focusedArtistId || undefined}
        aria-current={!focusedArtistId ? "true" : undefined}
        onClick={() => onSelect(null)}
      >
        <AllRosterTile orgId={orgId} />
        <span className={styles.allText}>
          <span className={styles.allTitle}>All roster</span>
          <span className={styles.allMeta}>Cross-portfolio view</span>
        </span>
        <span className={styles.allPill}>Scoped</span>
      </button>

      <div className={styles.railHead}>
        <span className={styles.railTitle}>Artists</span>
        {/* The count measures the rows BELOW it: it follows the search filter, so the header and the list it
            sits on can never describe different things. */}
        <span className={styles.railCount}>{isLoading ? "—" : filtered.length}</span>
      </div>

      {isError ? (
        <div className={styles.railEmpty}>
          Couldn't load the roster.{" "}
          <button type="button" className={styles.addBtn} onClick={onRetry}>
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className={styles.contentLoading} role="status" aria-live="polite">
          <Spinner size={18} label="Loading roster…" />
        </div>
      ) : artists.length === 0 ? (
        // Born-blank: a fresh org has NO artists until one links + assigns. Designed empty
        // state — NEVER placeholder artists.
        <div className={styles.railEmpty}>
          <p className={styles.railEmptyTitle}>No artists yet</p>
          <p className={styles.railEmptyText}>
            Artists appear here once they link your organization and assign you to their team.
          </p>
        </div>
      ) : filtered.length === 0 ? (
        <p className={styles.railEmpty}>No matches.</p>
      ) : (
        <div className={styles.list}>
          {filtered.map((a) => {
            const meta = artistMetaLine(a);
            return (
              <button
                key={a.bandId}
                type="button"
                className={styles.row}
                data-active={a.bandId === focusedArtistId || undefined}
                onClick={() => a.bandId && onSelect(a.bandId)}
              >
                <RailArtistMark artist={a} />
                <span className={styles.rowText}>
                  <span className={styles.rowNameRow}>
                    <span className={styles.rowName}>{a.name || "Artist"}</span>
                    {/* Per-ROW, from the server: an org route carries no band, so there is no ambient tier
                        here and every artist on the rail can be on a different plan. */}
                    {/* The real Pro badge, the lockup from the logo (ART-333) — never a text chip. */}
                    {a.planTier === "pro" && <ProBadge size={14} className={styles.rowPro} />}
                    <ManagerModeMark mode={a.managerMode} />
                  </span>
                  {meta && <span className={styles.rowMeta}>{meta}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.railFoot}>
        <button type="button" className={styles.addBtn} onClick={onAdd}>
          <Icon icon={Plus} size={16} /> Add artist
        </button>
      </div>
    </aside>
  );
}

// ---- The org account chip (identity + team/settings + multi-seat subview switch) ---------------------------

interface RosterAccountChipProps {
  orgId: string;
  /** The active org seat's role key — the chip's sub-line ("Mgmt Day To Day"). */
  seatRole: string | null | undefined;
  orgSeats: { orgId?: string | null; displayName?: string | null; root?: string | null }[];
  /**
   * `roster:manage_seats` — the ADMIN capability. It gates the whole Team surface AND the organization
   * half of settings, both of which are `roster:manage_seats` server-side (see below).
   */
  canManageTeam: boolean;
  /** Open the caller's OWN profile IN PLACE — never a route change (the BandSwitcher contract). */
  onViewProfile: () => void;
  /** Open the Team modal over the current page. */
  onOpenTeam: () => void;
  /** Open the org Settings modal over the current page. */
  onOpenSettings: () => void;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

/**
 * Who the person is, for the account chip: their display name, else first + last, else the local part of their
 * address. Mirrors legacy's `RosterAccountMenu.displayName` — a seat always renders SOMEONE, never a blank chip.
 */
function personName(person: { displayName?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null } | undefined): string {
  const display = person?.displayName?.trim();
  if (display) return display;
  const full = [person?.firstName, person?.lastName].filter(Boolean).join(" ").trim();
  if (full) return full;
  const local = person?.email?.split("@")[0];
  return local ? local[0]!.toUpperCase() + local.slice(1) : "Your account";
}

function RosterAccountChip({
  orgId,
  seatRole,
  orgSeats,
  canManageTeam,
  onViewProfile,
  onOpenTeam,
  onOpenSettings,
  onNavigate,
  onLogout,
}: RosterAccountChipProps) {
  const me = useMe();
  const seatRoles = useSeatRoles();
  const setActiveOrg = useActiveOrg((s) => s.setActiveOrg);
  // The chip is WHO YOU ARE, not which org — the org is the topbar's leading lockup, and naming it twice in one
  // row is what this replaced (PO, 2026-09-01). Legacy's RosterAccountMenu has always been the person too.
  const name = personName(me.data?.person);
  // A manual subview switcher renders ONLY for genuinely multi-seat users (the jedi-email gate dies).
  const others = orgSeats.filter((s) => s.orgId && s.orgId !== orgId);

  return (
    <AccountMenu
      // PersonAvatar, not Avatar: the chip is a PERSON, and `person.avatarUrl` — what this passed — is the
      // future CDN slot that no code path fills, so an uploaded photo showed on the profile dialog and as
      // initials in the top bar of the same page. The bytes ride the authenticated avatar route (GET /avatars/person/{id}),
      // which only the data-aware component knows how to fetch.
      avatar={
        <PersonAvatar
          personId={me.data?.person?.personId}
          avatarMediaId={me.data?.person?.avatarMediaId}
          initials={orgInitials(name)}
          size="xs"
        />
      }
      name={name}
      /* The seat's TITLE — what this person is called. Resolving a job label from the ROLE would render
         "Head of A&R" and "Marketing Associate" identically, since both grant label_staff. */
      sub={roleLabelFrom(seatRoles.data?.orgRoles, seatRole)}
      menuLabel="Your account and organization"
    >
      {/* The person-record door, first — the same opening row the artist shell's BandSwitcher has always
          carried, on the same primitive, with the signed-in address under it saying which account this is.
          Ungated on purpose: everyone has a profile, and this shell had no way to reach yours. */}
      <AccountMenuHeader
        label="View my profile"
        meta={me.data?.person?.email}
        // The PERSON's own face (ART-238) — a roster seat is always a person, so a circle. Person initials,
        // not the org's, since this row is the caller's profile.
        avatar={
          <PersonAvatar
            personId={me.data?.person?.personId}
            avatarMediaId={me.data?.person?.avatarMediaId}
            initials={initials(me.data?.person?.displayName || me.data?.person?.firstName)}
            size="md"
          />
        }
        onSelect={onViewProfile}
      />
      <AccountMenuDivider />
      {others.length > 0 && (
        <>
          <AccountMenuSection>Switch organization</AccountMenuSection>
          {others.map((s) => (
            <AccountMenuItem
              key={s.orgId}
              checked={false}
              onSelect={() => {
                setActiveOrg(s.orgId as string);
                onNavigate("/roster");
              }}
            >
              {s.displayName || "Organization"} · {subviewLabel(s.root)}
            </AccountMenuItem>
          ))}
          <AccountMenuDivider />
        </>
      )}
      {/* Team is an ADMIN surface: every read behind it is `roster:manage_seats`-gated, so without that
          capability this entry was a door onto a page whose only content was a 403 error card — which is
          exactly how it was reported ("loads an empty page"). A capability the seat does not hold renders
          no affordance at all, never a disabled one. It opens IN PLACE (the SettingsModal precedent);
          `/roster/team` survives as a full page for a bookmark. */}
      {canManageTeam && <AccountMenuItem onSelect={onOpenTeam}>Team</AccountMenuItem>}
      {/* ART-239: present only when something is waiting — opens the root-mounted Pending invites modal. */}
      <PendingInvitesMenuItem />
      {/* One dialog, named for what the seat actually gets. Its Organization tab is admin-only (PO,
          2026-09-01) — `PUT /orgs` is the same `roster:manage_seats` — but the Account tab is this shell's
          ONLY door to your own password, since it has no Settings gear. So the door stays for everyone and
          the LABEL follows the tabs behind it, the way `rosterTabLabel` already forks one surface's name per
          lens; a label promising the organization and delivering a password field is the failure to avoid. */}
      <AccountMenuItem onSelect={onOpenSettings}>
        {canManageTeam ? "Organization settings" : "Account settings"}
      </AccountMenuItem>
      <AccountMenuDivider />
      <AccountMenuItem tone="danger" onSelect={onLogout}>
        Log out
      </AccountMenuItem>
    </AccountMenu>
  );
}

/** Bare shell frame for the top-level error / no-org states (no nav/rail available). */
function ShellFrame({ children, onLogout }: { children: React.ReactNode; onLogout: () => void }) {
  const auth = useAuth();
  return (
    <div className={styles.shell}>
      <ShellTopbar>
        <div className={styles.brand}>
          <Wordmark size={22} />
        </div>
        <ShellTopbarActions>
          <Button variant="ghost" size="sm" onClick={() => (onLogout ? onLogout() : void auth.logout())}>
            Log out
          </Button>
        </ShellTopbarActions>
      </ShellTopbar>
      <main className={styles.content}>{children}</main>
    </div>
  );
}
